import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import dayjs from 'dayjs';
import {
	Button,
	CloseIcon,
	DatePicker,
	Divider,
	IconButton,
	Layout,
	Text,
	TextArea,
	TextField,
	TimePicker,
} from '@phonectas/unifon-ui-kit';
import useThreeSixtyNewStore from 'common/stores/threeSixtyStore';
import {shallow} from 'zustand/shallow';
import {isEmailValid} from 'utils/email';
import {
	InnsalgKontakt,
	InnsalgSvar,
	ThreeSixtyStoreState,
} from 'common/types/store/threeSixtyTypes';
import {
	DialogHeader,
	StyledCustomDialog,
	StyledDialogContent,
} from '../UpsellDialog/styled';
import {
	StyledFooter,
	StyledFormGrid,
	StyledPickerRow,
	StyledList,
	StyledManus,
	StyledSection,
	StyledTable,
} from './styled';

interface InnsalgDialogProps {
  show: boolean;
  onClose: () => void;
}

const EMPTY_KONTAKT: InnsalgKontakt = {
	navn: '',
	epost: '',
	telefon: '',
	rolle: '',
};

// Planleggeren rejects an invalid epost with 400, empty is allowed
const validateEpost = (value: string | number): boolean => !String(value).trim() || isEmailValid(String(value).trim());

const formatWait = (sec: number | null) =>
	sec === null || sec === undefined ? '-' : `${sec} s`;

const InnsalgDialog: React.FC<InnsalgDialogProps> = ({show, onClose}) => {
	const [t] = useTranslation();
	const {postInnsalgSvar} = useThreeSixtyNewStore((state) => ({postInnsalgSvar: state.postInnsalgSvar}), shallow);
	const {innsalg, isInnsalgLoading} = useThreeSixtyNewStore(
		(state: ThreeSixtyStoreState) => ({
			innsalg: state?.innsalg,
			isInnsalgLoading: state?.isInnsalgLoading,
		}),
	);
	const oppslag = innsalg?.vis ? innsalg : null;
	const originalKontakt: InnsalgKontakt = useMemo(
		() =>
			oppslag
				? {
					navn: oppslag.kontakt.navn,
					epost: oppslag.kontakt.epost,
					telefon: oppslag.kontakt.telefon,
					rolle: oppslag.kontakt.rolle,
				}
				: EMPTY_KONTAKT,
		[oppslag],
	);
	const [kontakt, setKontakt] = useState<InnsalgKontakt>(EMPTY_KONTAKT);
	const [notat, setNotat] = useState('');
	const [dato, setDato] = useState<Date | null>(null);
	const [tidspunkt, setTidspunkt] = useState('');

	// Reset the form for every new visning
	useEffect(() => {
		setKontakt(originalKontakt);
		setNotat('');
		setDato(null);
		setTidspunkt('');
	}, [originalKontakt]);

	const pickerRowRef = useRef<HTMLDivElement>(null);

	// The ui-kit Dialog closes on Escape by itself. Escape meant for the date/time picker must not close the
	// dialog (and lose the form): react-datepicker marks it with preventDefault, the TimePicker only has focus
	const onDialogClose = useCallback(
		(e?: React.SyntheticEvent | KeyboardEvent) => {
			if (e && 'key' in e && e.key === 'Escape' &&
				(e.defaultPrevented || pickerRowRef.current?.contains(document.activeElement))) return;
			onClose();
		},
		[onClose],
	);

	const onKontaktChange = useCallback(
		(field: keyof InnsalgKontakt) =>
			(e: React.ChangeEvent<HTMLInputElement>) => {
				const value = e.target.value;
				setKontakt((prev) => ({...prev, [field]: value}));
			},
		[],
	);

	const onAnswer = useCallback(
		(svar: InnsalgSvar['svar']) => {
			if (!oppslag || !validateEpost(kontakt.epost)) return;
			const kontaktEndret = (
        Object.keys(originalKontakt) as (keyof InnsalgKontakt)[]
			).some((key) => kontakt[key].trim() !== originalKontakt[key].trim());
			const data: InnsalgSvar = {
				svar,
				kontakt: {
					navn: kontakt.navn.trim(),
					epost: kontakt.epost.trim(),
					telefon: kontakt.telefon.trim(),
					rolle: kontakt.rolle.trim(),
				},
				kontakt_endret: kontaktEndret,
			};
			if (svar === 'ja') {
				if (notat.trim()) data.notat = notat.trim();
				if (dato) {
					data.dato = dayjs(dato).format('YYYY-MM-DD');
					// TimePicker allows free text, only send a valid TT:MM
					const time = tidspunkt.trim().match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
					if (time?.[1] && time[2]) data.tidspunkt = `${time[1].padStart(2, '0')}:${time[2]}`;
				}
			}
			postInnsalgSvar({
				visningId: oppslag.visning_id,
				data,
				onSuccess: onClose,
			});
		},
		[
			oppslag,
			originalKontakt,
			kontakt,
			notat,
			dato,
			tidspunkt,
			postInnsalgSvar,
			onClose,
		],
	);

	if (!oppslag) return null;

	const isEpostInvalid = !validateEpost(kontakt.epost);
	const {kodata, snakkepunkter} = oppslag;
	const kildeText =
    oppslag.kontakt.kilde === 'mersalgslisten'
    	? t('From the customer list')
    	: oppslag.kontakt.kilde === 'unifon360'
    		? `${t('Corrected in Unifon 360')} ${oppslag.kontakt.oppdatert ?? ''}`.trim()
    		: t('We have no contact person - please fill in');

	return (
		<StyledCustomDialog
			show={show}
			onClose={onDialogClose}
			$width={900}
			$maxHeight={90}
			$noPadding
		>
			<StyledDialogContent>
				<DialogHeader>
					<Text variant="h1">{oppslag.tittel}</Text>
					<IconButton onClick={onClose} aria-label={t('Close dialog')}>
						<CloseIcon />
					</IconButton>
				</DialogHeader>
				<StyledSection>
					<Text variant="body1" color="textSecondary">
						{oppslag.kunde.navn}
					</Text>
					<StyledManus>{oppslag.tekst}</StyledManus>
					{snakkepunkter && snakkepunkter.length > 0 && (
						<StyledList>
							{snakkepunkter.map((punkt, index) => (
								<li key={index}>
									<Text>{punkt}</Text>
								</li>
							))}
						</StyledList>
					)}
				</StyledSection>
				{kodata && (
					<>
						<Divider />
						<StyledSection>
							<Layout justifyContent="space-between" fullWidth nowrap>
								<Text variant="h3">{t('Queue data')}</Text>
								{kodata.oppdatert && (
									<Text variant="body1" color="textSecondary">
										{`${t('Updated')} ${kodata.oppdatert}`}
									</Text>
								)}
							</Layout>
							<StyledTable>
								<thead>
									<tr>
										<th>{t('Queue')}</th>
										<th className="num">{t('Calls')}</th>
										<th className="num">{t('Unanswered')}</th>
										<th className="num">{t('Answer rate')}</th>
										<th className="num">{t('Avg. wait')}</th>
									</tr>
								</thead>
								<tbody>
									<tr className="total">
										<td>{`${t('Total')} (${kodata.antall_koer})`}</td>
										<td className="num">{kodata.anrop}</td>
										<td className="num">{kodata.ubesvarte}</td>
										<td className="num">{`${kodata.svarprosent} %`}</td>
										<td className="num">
											{formatWait(kodata.snitt_ventetid_sek)}
										</td>
									</tr>
									{kodata.koer.map((ko) => (
										<tr key={`${ko.nummer}-${ko.navn}`}>
											<td>{ko.navn}</td>
											<td className="num">{ko.anrop}</td>
											<td className="num">{ko.ubesvarte}</td>
											<td className="num">{`${ko.svarprosent} %`}</td>
											<td className="num">
												{formatWait(ko.snitt_ventetid_sek)}
											</td>
										</tr>
									))}
								</tbody>
							</StyledTable>
						</StyledSection>
					</>
				)}
				<Divider />
				<StyledSection>
					<Layout justifyContent="space-between" fullWidth nowrap>
						<Text variant="h3">{t('Contact person')}</Text>
						<Text variant="body1" color="textSecondary">
							{kildeText}
						</Text>
					</Layout>
					<StyledFormGrid>
						<TextField
							name="navn"
							label={t('Name')}
							value={kontakt.navn}
							onChange={onKontaktChange('navn')}
							inputProps={{maxLength: 255}}
							fullWidth
						/>
						<TextField
							name="rolle"
							label={t('Role')}
							value={kontakt.rolle}
							onChange={onKontaktChange('rolle')}
							inputProps={{maxLength: 255}}
							fullWidth
						/>
						<TextField
							name="epost"
							label={t('Email')}
							value={kontakt.epost}
							onChange={onKontaktChange('epost')}
							inputProps={{maxLength: 255, type: 'email'}}
							validator={validateEpost}
							invalidMessage={t('Enter a valid email address')}
							fullWidth
						/>
						<TextField
							name="telefon"
							label={t('Phone')}
							value={kontakt.telefon}
							onChange={onKontaktChange('telefon')}
							inputProps={{maxLength: 32}}
							fullWidth
						/>
					</StyledFormGrid>
				</StyledSection>
				<Divider />
				<StyledSection>
					<Text variant="h3">{t('Meeting (optional)')}</Text>
					<StyledPickerRow ref={pickerRowRef}>
						<DatePicker
							label={t('Preferred date')}
							placeholder={t('Select date')}
							selected={dato}
							onChange={(date: Date | null) => {
								setDato(date);
								if (!date) setTidspunkt('');
							}}
							minDate={new Date()}
							maxDate={dayjs().add(1, 'year').toDate()}
							todayLabel={t('Today')}
							tomorrowLabel={t('Tomorrow')}
						/>
						<TimePicker
							label={t('Preferred time')}
							placeholder={t('Select time')}
							value={tidspunkt}
							onChange={(value) => setTidspunkt(value ? String(value) : '')}
							disabled={!dato}
						/>
					</StyledPickerRow>
					<TextArea
						name="notat"
						label={t('Note')}
						value={notat}
						onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
							setNotat(e.target.value)
						}
						inputProps={{maxLength: 2000}}
						rows={3}
						fullWidth
					/>
				</StyledSection>
				<Divider />
				<StyledFooter>
					<Button
						variant="secondary"
						color="error"
						disabled={isInnsalgLoading || isEpostInvalid}
						onClick={() => onAnswer('nei')}
					>
						{t('No thanks')}
					</Button>
					<Button
						variant="primary"
						color="success"
						disabled={isInnsalgLoading || isEpostInvalid}
						onClick={() => onAnswer('ja')}
					>
						{dato ? t('Yes, book meeting') : t('Yes please')}
					</Button>
				</StyledFooter>
			</StyledDialogContent>
		</StyledCustomDialog>
	);
};

export default InnsalgDialog;
